import { useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { T } from "./lib/theme";
import { supabase } from "./lib/supabase";
import { useSettings } from "./lib/settings";
import type { Meeting, MemberSummary } from "./lib/types";
import { useAccess, useSession } from "./hooks/useSession";
import {
  useClearConsidering,
  useImportMembers,
  useMeetings,
  useMembers,
  useSaveMeeting,
  useSaveMember,
  useToggleConsidering,
  type MemberInput,
  type SaveMeetingInput,
} from "./hooks/useData";
import { ErrorState, ScrollTopButton, Spinner, Toast } from "./components/ui";
import { TabBar, type Tab } from "./components/TabBar";
import { UpNext } from "./screens/UpNext";
import { RecordScreen } from "./screens/Record";
import { Meetings as MeetingsScreen } from "./screens/Meetings";
import { Members } from "./screens/Members";
import { SettingsSheet } from "./screens/SettingsSheet";
import { NoAccess, SignIn } from "./screens/SignIn";
import { fmtDate } from "./lib/dates";

export default function App() {
  const { session, loading: sessionLoading } = useSession();
  const access = useAccess(Boolean(session));

  if (sessionLoading) return <FullPageSpinner />;
  if (!session) return <SignIn />;
  if (access.isLoading) return <FullPageSpinner />;
  if (access.isError)
    return (
      <div style={{ minHeight: "100vh", background: T.paper, paddingTop: 60 }}>
        <ErrorState error={access.error} onRetry={() => void access.refetch()} />
      </div>
    );
  if (!access.data)
    return <NoAccess email={session.user.email ?? ""} onSignOut={() => void supabase.auth.signOut()} />;

  return <Signed session={session} />;
}

function FullPageSpinner() {
  return (
    <div style={{ minHeight: "100vh", background: T.navy, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="spinner light" aria-label="Loading" />
    </div>
  );
}

function Signed({ session }: { session: Session }) {
  const { settings, set } = useSettings();

  const membersQ = useMembers();
  const meetingsQ = useMeetings();

  const saveMeeting = useSaveMeeting();
  const saveMember = useSaveMember();
  const importMembers = useImportMembers();
  const toggleConsidering = useToggleConsidering();
  const clearConsidering = useClearConsidering();

  const [tab, setTab] = useState<Tab>("upnext");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editMeeting, setEditMeeting] = useState<Meeting | null>(null);
  const [prefillDate, setPrefillDate] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; tone?: "error" } | null>(null);

  const flash = (msg: string, tone?: "error") => {
    setToast({ msg, tone });
    window.setTimeout(() => setToast(null), 2800);
  };

  const members: MemberSummary[] = membersQ.data ?? [];
  const meetings: Meeting[] = meetingsQ.data ?? [];
  const consideringCount = members.filter((m) => m.is_considering).length;

  const loading = membersQ.isLoading || meetingsQ.isLoading;
  const error = membersQ.error ?? meetingsQ.error;

  const goTab = (t: Tab) => {
    if (t !== "record") setEditMeeting(null);
    setTab(t);
  };

  const onSaveMeeting = (input: SaveMeetingInput) =>
    saveMeeting.mutate(input, {
      onSuccess: () => {
        flash(`Saved ${fmtDate(input.date)}`);
        setEditMeeting(null);
        setPrefillDate(null);
        setTab("meetings");
      },
      onError: (e) => flash(e instanceof Error ? e.message : "Couldn't save", "error"),
    });

  const onSaveMember = (m: MemberInput) =>
    saveMember.mutate(m, {
      onSuccess: () => flash(m.id ? "Member updated" : "Member added"),
      onError: (e) => flash(e instanceof Error ? e.message : "Couldn't save", "error"),
    });

  const onImport = (rows: Array<Omit<MemberInput, "id">>) =>
    importMembers.mutate(rows, {
      onSuccess: (n) => flash(`Imported ${n} member${n === 1 ? "" : "s"}`),
      onError: (e) => flash(e instanceof Error ? e.message : "Couldn't import", "error"),
    });

  return (
    <div style={{ background: T.paper, minHeight: "100vh" }}>
      <div style={{ maxWidth: 520, margin: "0 auto", paddingBottom: 78, position: "relative" }}>
        {loading && <Spinner label="Loading the ward…" />}
        {!loading && error && <ErrorState error={error} onRetry={() => { void membersQ.refetch(); void meetingsQ.refetch(); }} />}

        {!loading && !error && (
          <>
            {tab === "upnext" && (
              <UpNext
                members={members}
                meetings={meetings}
                settings={settings}
                onToggleStar={(m) => toggleConsidering.mutate({ memberId: m.id, on: !m.is_considering })}
                onManage={() => setTab("members")}
                onSettings={() => setSettingsOpen(true)}
              />
            )}

            {tab === "record" && (
              <RecordScreen
                key={editMeeting ? `edit-${editMeeting.id}` : prefillDate ?? "new"}
                members={members}
                meetings={meetings}
                settings={settings}
                editing={editMeeting}
                prefillDate={prefillDate}
                saving={saveMeeting.isPending}
                onSave={onSaveMeeting}
                onCancelEdit={() => {
                  setEditMeeting(null);
                  setTab("meetings");
                }}
              />
            )}

            {tab === "meetings" && (
              <MeetingsScreen
                meetings={meetings}
                members={members}
                onFillGap={(d) => {
                  setPrefillDate(d);
                  setEditMeeting(null);
                  setTab("record");
                }}
                onEdit={(m) => {
                  setEditMeeting(m);
                  setPrefillDate(null);
                  setTab("record");
                }}
              />
            )}

            {tab === "members" && (
              <Members
                members={members}
                meetings={meetings}
                saving={saveMember.isPending || importMembers.isPending}
                onBack={() => setTab("upnext")}
                onSave={onSaveMember}
                onImport={onImport}
              />
            )}
          </>
        )}
      </div>

      {settingsOpen && (
        <SettingsSheet
          session={session}
          settings={settings}
          set={set}
          consideringCount={consideringCount}
          onClearConsidering={() =>
            clearConsidering.mutate(undefined, { onSuccess: () => flash("Considering list cleared") })
          }
          onManage={() => {
            setSettingsOpen(false);
            setTab("members");
          }}
          onSignOut={() => void supabase.auth.signOut()}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      {toast && <Toast message={toast.msg} tone={toast.tone} />}

      <ScrollTopButton raised={tab === "members"} />
      <TabBar tab={tab} setTab={goTab} />
    </div>
  );
}
